import { get, merge, isPlainObject, memoize, mapValues } from 'lodash';
import numbro from 'numbro';
import languages from 'numbro/dist/languages.min';
import format from 'date-fns/format';

import {
  defaultLocale,
  defaultTranslations,
  languageToLocaleMap,
  locale,
  supportedLocales
} from 'config';

import store from 'store';

import User from 'models/user';

export const translations = defaultTranslations;

// This is to guarantee that deviceName is passed in always
const mapTranslations = (source) =>
  mapValues(source, (translation) => {
    if (isPlainObject(translation)) {
      return mapTranslations(translation);
    }

    if (translation.setup) {
      return translation;
    }

    const newTranslation = (context = {}) => {
      const user = User.selectFromState(store.getState());
      return translation({ deviceName: user.deviceName, ...context });
    };

    // Used to bypass deviceName usage and circular logic
    newTranslation.source = translation;

    newTranslation.setup = true;
    return newTranslation;
  });

const setupDefaults = () => merge(translations, mapTranslations(translations));

setupDefaults();

const setupTranslations = (t) => merge(translations, t);

export const getFallbackLocale = (locale) => {
  const languageCode = locale.split('-')[0];
  return languageToLocaleMap[languageCode] || defaultLocale;
};

const localeToSupportedLocale = (locale) => {
  if (supportedLocales.includes(locale)) {
    return locale;
  }

  return getFallbackLocale(locale);
};

let loadPromise;
let i18nDate;

const dateFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric'
};

// Loader allows async fetch of translations
export const load = () => {
  if (!loadPromise) {
    const supportedLocale = localeToSupportedLocale(locale);

    // Default locale is already loaded
    if (supportedLocale === defaultLocale) {
      loadPromise = Promise.resolve(translations);
    } else {
      loadPromise = import(`translations/${supportedLocale}.json`)
        .then(setupTranslations)
        .then(setupDefaults);
    }
    if (languages[supportedLocale]) {
      numbro.registerLanguage(languages[supportedLocale]);
      numbro.setLanguage(supportedLocale);
    }
    if (Intl && Intl.DateTimeFormat) {
      i18nDate = new Intl.DateTimeFormat(supportedLocale, dateFormatOptions);
    }
  }
  return loadPromise;
};

export const formatDate = (dateObject) => {
  if (i18nDate) {
    return i18nDate.format(dateObject);
  }

  // Fallback to original logic
  return format(dateObject, 'MMMM D, YYYY');
};

export default (field) => {
  const value = get(translations, field);
  if (!value) {
    throw new Error(`Field ${field} does not exist for locale ${locale}`);
  }
  return value;
};

// Translate all the fields in a subsection
export const translateAll = memoize((section) =>
  mapValues(section, (value) => value())
);
